import { computed, onUnmounted, ref, type ComputedRef, type Ref } from 'vue';
import backendApis from '@/utils/backendApis';
import { logDebug, logError, logWarn } from '@/utils/logger';
import { sendOverlayError, OVERLAY_ERROR } from '@/utils/nacxMetrics';

const appInstance = import.meta.env.VITE_APP_ENV;
const catchErrorPrefix = import.meta.env.VITE_APP_CATCH_ERROR_PREFIX;

// // How long to wait after a vote before reconciling with the backend, and
// // (if that reconciliation came back stale) how long to wait before the one
// // bounded retry. Both currently 1s; kept as separate constants since
// // there's no reason they need to stay equal.
const GRACEFUL_UPDATE_DELAY_MS = 1000;
const GRACEFUL_UPDATE_RETRY_DELAY_MS = 1000;

/**
 * A row from the backend's `results` array. Real rows carry `ibUuid` plus one
 * numeric (sometimes string-typed, see extractResultsArray) field per poll
 * option, keyed by the option's backend field name — e.g. `option1`/`option2`
 * for PollSimpleOptions or `heroOption1`/`heroOption2` for PollHeroOptions.
 * The option keys vary per poll type, so they can't be named individually
 * here; ibUuid is the one field that's always present and worth naming.
 */
export interface PollDataRow {
  ibUuid?: string;
  [optionKey: string]: number | string | undefined;
}

export interface UsePollResultsOptions {
  /** Ordered option keys matching backend field names, e.g. Object.values(PollSimpleOptions). */
  optionKeys: string[];
  /** Accessor for the live breakUuid (not a Ref on the setup store). */
  getBreakUuid: () => string;
  /** Accessor for the live AWS credential provider. */
  getAwsCredentialProvider: () => unknown;
  /**
   * How failures/anomalies detected by this composable get reported:
   * - 'nacx': structured OVERLAY_ERROR codes via sendOverlayError — use for
   *   overlays that already initialize NACX metrics (currently only
   *   TempPollStreamOverlay).
   * - 'beacon': a catchErrorPrefix `/beaconError` fetch ping, same convention
   *   beaconManager.ts already uses for its own internal warnings — use for
   *   overlays that report via VAST beacons instead of NACX.
   * - 'none': don't report.
   * Defaults to 'beacon' since every poll overlay has a breakUuid to report
   * against, whether or not it's wired up to NACX.
   */
  errorReporting?: 'nacx' | 'beacon' | 'none';
}

export interface GetSelectionResult {
  /** True if pollPercentages/rawResults were updated from this GET. */
  accepted: boolean;
  /** True only when the guard discarded a stale response (not a network error). */
  discarded: boolean;
}

export interface UsePollResultsReturn {
  calculatedPollPercentages: ComputedRef<number[]>;
  rawResults: Ref<PollDataRow[]>;
  initialGetCheckDone: Ref<boolean>;
  getSelectionHandler: (source?: string) => Promise<GetSelectionResult>;
  putSelectionHandler: (optionLabel: string) => Promise<void>;
  applyOptimisticVote: (index: number) => number[];
  scheduleGracefulUpdate: () => void;
  /**
   * Full vote flow used by all 4 poll overlays' buttonSelectHandler: sends
   * the vote to the backend (fetching results first if they haven't loaded
   * yet), applies the optimistic result, and schedules the graceful update +
   * bounded retry. Ignores a second call within the same session (e.g. a
   * mobile "poke" firing again before the first vote's network calls have
   * resolved) rather than double-submitting the vote or double-counting the
   * optimistic result.
   */
  castVote: (index: number, optionLabel: string) => Promise<number[]>;
}

// // Pure calc helpers — exported for unit testing, same convention as useOverlayTimeline.ts.

/**
 * Raw per-option vote counts from a backend poll-data row, optionally with
 * one option optimistically incremented by 1 (used for the pre-confirmation
 * "assumed" vote display).
 */
export const extractResultsArray = (
  pollResults: PollDataRow[],
  optionKeys: string[],
  incrementIndex?: number,
): number[] => {
  const data = pollResults?.[0];

  return optionKeys.map((option, index) => {
    const optionValue = data ? data[option] : undefined;
    // // Handle both string and number values in case we get back from db
    let value = optionValue ? Number(optionValue) : 0;
    if (incrementIndex !== undefined && index === incrementIndex) {
      value = value + 1;
    }
    return value;
  });
};

/**
 * Percentages (0-100, rounded, remainder-corrected so the sum is exactly 100)
 * from a raw per-option results array. All-zero input returns all-zero
 * percentages (avoids division by zero).
 */
export const toPercentages = (resultsArray: number[]): number[] => {
  const grandTotal = resultsArray.reduce((sum, result) => sum + result, 0);

  if (grandTotal === 0) {
    return resultsArray.map(() => 0);
  }

  const rawPercentages = resultsArray.map((result) => (100 * result) / grandTotal);
  const percentageArray = rawPercentages.map((percentage) => Math.round(percentage));

  const sum = percentageArray.reduce((a, b) => a + b, 0);
  if (sum !== 100 && sum !== 0) {
    let maxDecimalIndex = 0;
    let maxDecimalPart = rawPercentages[0] - Math.floor(rawPercentages[0]);

    for (let i = 1; i < rawPercentages.length; i++) {
      const decimalPart = rawPercentages[i] - Math.floor(rawPercentages[i]);
      if (decimalPart > maxDecimalPart) {
        maxDecimalPart = decimalPart;
        maxDecimalIndex = i;
      }
    }

    percentageArray[maxDecimalIndex] += 100 - sum;
  }

  return percentageArray;
};

/** extractResultsArray + toPercentages in one call — drop-in for the old per-component calculateResults. */
export const calculateResults = (
  pollResults: PollDataRow[],
  optionKeys: string[],
  incrementIndex?: number,
): number[] => toPercentages(extractResultsArray(pollResults, optionKeys, incrementIndex));

/**
 * Shared vote/fetch/reconcile logic for the 4 live poll overlays. Guards
 * against a stale post-vote GET (backend not yet caught up) overwriting a
 * correct optimistic result with a regressed one, and retries once so the
 * UI still reconciles with real backend truth shortly after.
 *
 * The regression-guard floor (lastKnownGoodTotal) is intentionally permanent
 * for the lifetime of the component, not just the retry window: if the
 * backend genuinely lost the vote, falling back to trusting later GETs would
 * mean flipping the display to 0/0/0 sometime after the fact, which is worse
 * than holding the optimistic value. Each poll overlay is a fresh component
 * mount per break (a new iframe load from the TV parent), so this state
 * doesn't leak across breaks — a reload always starts the guard fresh.
 */
export function usePollResults(options: UsePollResultsOptions): UsePollResultsReturn {
  const { optionKeys, getBreakUuid, getAwsCredentialProvider, errorReporting = 'beacon' } = options;

  const dummyOutput = () => optionKeys.map(() => 0);

  const rawResults = ref<PollDataRow[]>([]) as Ref<PollDataRow[]>;
  const pollPercentagesRef = ref<number[]>(dummyOutput());
  const initialGetCheckDone = ref(false);

  // // Regression-guard state: lastKnownGoodTotal === null means "guard inactive" (pre-vote).
  const hasVotedInSession = ref(false);
  const lastKnownGoodTotal = ref<number | null>(null);

  // // Separate from hasVotedInSession: set synchronously at the top of
  // // castVote (before any await), so a second castVote call that arrives
  // // while the first vote's GET/PUT is still in flight (e.g. a mobile poke
  // // firing twice) is blocked immediately, not just once applyOptimisticVote
  // // eventually runs.
  const hasCastVote = ref(false);

  let gracefulUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
  let gracefulRetryTimeout: ReturnType<typeof setTimeout> | null = null;

  const calculatedPollPercentages = computed(() => pollPercentagesRef.value);

  const reportError = (code: string, message: string): void => {
    if (errorReporting === 'none') return;

    if (errorReporting === 'nacx') {
      sendOverlayError(code, message);
      return;
    }

    // // 'beacon' — same catchErrorPrefix + /beaconError convention
    // // beaconManager.ts already uses for its own internal warnings, for
    // // overlays that report via VAST beacons instead of NACX.
    fetch(
      `${catchErrorPrefix}${appInstance}/beaconError?ib_uuid=${getBreakUuid()}&warning=${encodeURIComponent(`[${code}] ${message}`)}`,
    ).catch((fetchError) =>
      logWarn('[usePollResults] Failed to send beacon error ping', fetchError),
    );
  };

  const getSelectionHandler = async (source = 'unknown'): Promise<GetSelectionResult> => {
    logDebug(`getSelectionHandler called from: ${source}`);

    try {
      const capturedJson = await backendApis.getPollData(
        getBreakUuid(),
        getAwsCredentialProvider(),
      );
      logDebug(`backendApis.getPollData() SUCCESS (called from: ${source})`);

      if (capturedJson) {
        const newResultsArray = extractResultsArray(capturedJson, optionKeys);
        const newTotal = newResultsArray.reduce((sum, result) => sum + result, 0);
        const newPercentages = toPercentages(newResultsArray);

        const isRegression =
          hasVotedInSession.value &&
          lastKnownGoodTotal.value !== null &&
          newTotal < lastKnownGoodTotal.value;

        if (isRegression) {
          logWarn('[usePollResults] Discarding stale/regressed GET result', {
            source,
            previousTotal: lastKnownGoodTotal.value,
            rejectedTotal: newTotal,
            previousPercentages: pollPercentagesRef.value,
            rejectedPercentages: newPercentages,
          });
          return { accepted: false, discarded: true };
        }

        rawResults.value = capturedJson;
        initialGetCheckDone.value = true;
        pollPercentagesRef.value = newPercentages;
        if (hasVotedInSession.value) {
          lastKnownGoodTotal.value = Math.max(lastKnownGoodTotal.value ?? 0, newTotal);
        }

        logDebug(
          `GET results - calculateResults(capturedJson): ${newPercentages} (called from: ${source})`,
        );

        return { accepted: true, discarded: false };
      } else {
        if (hasVotedInSession.value) {
          logWarn(
            '[usePollResults] Discarding stale/empty GET result (no data) after a vote was cast',
            {
              source,
              previousTotal: lastKnownGoodTotal.value,
              previousPercentages: pollPercentagesRef.value,
            },
          );
          return { accepted: false, discarded: true };
        }

        pollPercentagesRef.value = dummyOutput();
        logDebug(`GET results - dummyOutput: ${pollPercentagesRef.value} (called from: ${source})`);
        return { accepted: true, discarded: false };
      }
    } catch (errorResponse) {
      logError(`GET results CATCH - backendApis.getPollData() FAILURE (called from: ${source})`);
      logError(errorResponse);
      reportError(
        OVERLAY_ERROR.DATA_LOAD_FAILED,
        `getPollData failed (source: ${source}): ${String(errorResponse)}`,
      );
      return { accepted: false, discarded: false };
    }
  };

  const putSelectionHandler = async (optionLabel: string): Promise<void> => {
    try {
      await backendApis.putPollData(getBreakUuid(), optionLabel, getAwsCredentialProvider());
    } catch (errorResponse) {
      logError('PUT HANDLER - CATCH - backendApis.putPollData() FAILURE');
      logError(errorResponse);
      reportError(OVERLAY_ERROR.DATA_LOAD_FAILED, `putPollData failed: ${String(errorResponse)}`);
    }
  };

  const applyOptimisticVote = (index: number): number[] => {
    // // Defense-in-depth for any direct caller (castVote guards separately,
    // // before this ever runs) — a second call this session is a no-op that
    // // keeps the first vote's result rather than recomputing from the same
    // // (unchanged) rawResults snapshot and silently discarding it.
    if (hasVotedInSession.value) {
      logWarn(
        '[usePollResults] applyOptimisticVote called again this session — ignoring duplicate vote and keeping the first result',
        { index, previousPercentages: pollPercentagesRef.value },
      );
      return pollPercentagesRef.value;
    }

    const incrementedArray = extractResultsArray(rawResults.value, optionKeys, index);
    const assumedTotal = incrementedArray.reduce((sum, result) => sum + result, 0);
    const assumedPercentages = toPercentages(incrementedArray);

    pollPercentagesRef.value = assumedPercentages;
    hasVotedInSession.value = true;
    lastKnownGoodTotal.value = Math.max(lastKnownGoodTotal.value ?? 0, assumedTotal);

    return assumedPercentages;
  };

  const runGracefulUpdate = async (attempt: 1 | 2): Promise<void> => {
    if (attempt === 1) {
      gracefulUpdateTimeout = null;
    } else {
      gracefulRetryTimeout = null;
    }

    if (!hasVotedInSession.value) {
      logDebug('Graceful Update - Skipping graceful update: user did not vote in this session');
      return;
    }

    const source = attempt === 1 ? 'gracefulUpdate' : 'gracefulUpdateRetry';

    try {
      const result = await getSelectionHandler(source);

      if (result.discarded) {
        if (attempt === 1) {
          logDebug(
            `[usePollResults] Graceful update discarded as stale — scheduling one retry at +${GRACEFUL_UPDATE_RETRY_DELAY_MS}ms`,
          );
          reportError(
            OVERLAY_ERROR.POLL_RESULTS_STALE_AFTER_VOTE,
            'Backend had not recorded/returned the vote within 1s of casting it — retrying once',
          );
          gracefulRetryTimeout = setTimeout(() => {
            void runGracefulUpdate(2);
          }, GRACEFUL_UPDATE_RETRY_DELAY_MS);
        } else {
          logWarn(
            '[usePollResults] Graceful update retry still stale after 2 attempts — backend has not caught up',
            {
              previousTotal: lastKnownGoodTotal.value,
              previousPercentages: pollPercentagesRef.value,
            },
          );
          reportError(
            OVERLAY_ERROR.POLL_RESULTS_STALE_AFTER_RETRY,
            'Backend still had not recorded/returned the vote after a retry — giving up and keeping the optimistic result',
          );
        }
      } else if (result.accepted) {
        if (attempt === 1) {
          logDebug('Graceful Update - Successful');
        } else {
          logDebug(
            '[usePollResults] Graceful update retry recovered — reconciled with backend after an initial stale response',
          );
        }
      }
    } catch (error) {
      logWarn('Graceful Update - Failed to update percentages', error);
    }
  };

  const scheduleGracefulUpdate = (): void => {
    if (gracefulUpdateTimeout) {
      clearTimeout(gracefulUpdateTimeout);
      gracefulUpdateTimeout = null;
    }
    if (gracefulRetryTimeout) {
      clearTimeout(gracefulRetryTimeout);
      gracefulRetryTimeout = null;
    }

    logDebug('Setting up graceful update timeout');
    gracefulUpdateTimeout = setTimeout(() => {
      void runGracefulUpdate(1);
    }, GRACEFUL_UPDATE_DELAY_MS);
  };

  const castVote = async (index: number, optionLabel: string): Promise<number[]> => {
    if (hasCastVote.value) {
      logWarn('[usePollResults] castVote ignored — a vote was already cast this session', {
        index,
        optionLabel,
      });
      return pollPercentagesRef.value;
    }
    // // Set synchronously, before any await, so a second castVote call that
    // // arrives while this one is still awaiting GET/PUT (e.g. a mobile poke
    // // firing twice) is blocked immediately rather than also reaching the
    // // backend.
    hasCastVote.value = true;

    if (initialGetCheckDone.value) {
      logDebug('Results already loaded, proceed to send vote');
      await putSelectionHandler(optionLabel);
    } else {
      logDebug('Results not received yet, going to run again');
      await getSelectionHandler('buttonSelectHandler');
      await putSelectionHandler(optionLabel);
    }

    const assumedPercentages = applyOptimisticVote(index);
    logDebug(`Interstitial Results: ${JSON.stringify(assumedPercentages)}`);

    scheduleGracefulUpdate();

    return assumedPercentages;
  };

  onUnmounted(() => {
    if (gracefulUpdateTimeout) clearTimeout(gracefulUpdateTimeout);
    if (gracefulRetryTimeout) clearTimeout(gracefulRetryTimeout);
  });

  return {
    calculatedPollPercentages,
    rawResults,
    initialGetCheckDone,
    getSelectionHandler,
    putSelectionHandler,
    applyOptimisticVote,
    scheduleGracefulUpdate,
    castVote,
  };
}
